import React from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Users, Megaphone, BarChart3, Settings, LogOut, FileText, Wrench } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { clearAccessToken } from "@/api/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDevShortcuts } from "@/hooks/useDevShortcuts";

/** ------------------------------------------------------------
 * Polling support (context + hook + helper)
 * -----------------------------------------------------------*/
type PollingState = {
  enabled: boolean;
  intervalMs: number;
  toggle: () => void;
  cycleInterval: () => void; // 15s -> 30s -> 60s -> 15s
};

const PollingContext = React.createContext<PollingState | null>(null);

export function usePolling() {
  const ctx = React.useContext(PollingContext);
  if (!ctx) throw new Error("usePolling must be used within <HoaAdminLayout />");
  return ctx;
}

export function getPollingQueryOpts(enabled: boolean, intervalMs: number) {
  return {
    refetchInterval: enabled ? intervalMs : false,
    refetchOnWindowFocus: enabled,
  } as const;
}

/** ------------------------------------------------------------
 * Layout
 * -----------------------------------------------------------*/
const PRIMARY = "#1F3A93"; // brand blue
const BG = "#F5F6FA";

export default function HoaAdminLayout() {
  const [enabled, setEnabled] = React.useState(true);
  const [intervalMs, setIntervalMs] = React.useState(30000);
  const toggle = React.useCallback(() => setEnabled((s) => !s), []);
  const cycleInterval = React.useCallback(
    () => setIntervalMs((cur) => (cur === 15000 ? 30000 : cur === 30000 ? 60000 : 15000)),
    []
  );
  const pollingValue = React.useMemo<PollingState>(
    () => ({ enabled, intervalMs, toggle, cycleInterval }),
    [enabled, intervalMs, toggle, cycleInterval]
  );

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useLanguage();
  
  // Enable dev shortcuts (Ctrl+Shift+L to open logs)
  useDevShortcuts();

  async function handleLogout() {
    try {
      // Call logout (backend + clear tokens + clear sessionStorage)
      await logout();
      
      // Clear sessionStorage (HOA admin uses sessionStorage, NOT localStorage)
      sessionStorage.clear();
      
      // Clear in-memory token
      clearAccessToken();
      
      // Force full page reload to clear browser cache and prevent race conditions
      window.location.href = "/#/login";
    } catch (error) {
      console.error("Logout failed:", error);
      
      // Force logout even if everything fails
      sessionStorage.clear();
      clearAccessToken();
      
      // Force reload
      window.location.href = "/#/login";
    }
  }

  return (
    <PollingContext.Provider value={pollingValue}>
      <div className="min-h-screen flex flex-col relative" style={{ backgroundColor: BG }}>
        {/* Ambient background */}
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1000px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

        {/* Header */}
        <header className="sticky top-0 z-40">
          <div className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
            <div className="max-w-5xl mx-auto px-4 py-2.5">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <h1 className="text-[17px] font-semibold leading-tight tracking-[-0.01em]">
                  {titleFromPath(pathname, t)}
                </h1>
                <div className="flex items-center gap-2 text-[13px] text-gray-500">
                  <span>{t("hoa.layout.console")}</span>
                  <span>•</span>
                  <span>{t("roles.hoaAdmin")}</span>
                </div>
              </div>

              <Button
                size="sm"
                onClick={handleLogout}
                title={t("common.logout")}
                className="gap-2 rounded-xl bg-white text-gray-900 ring-1 ring-black/5 border-0 hover:bg-white active:bg-gray-50"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">{t("common.logout")}</span>
              </Button>
            </div>
          </div>
        </div>
        </header>

        {/* Main */}
        <main className="flex-1">
          <div className="max-w-5xl mx-auto px-4 py-4 pb-28 sm:pb-32">
            <Outlet />
          </div>
        </main>

        {/* Floating bottom dock */}
        <nav className="fixed bottom-3 left-0 right-0 z-40 pb-[env(safe-area-inset-bottom)]">
          <div className="mx-auto max-w-5xl px-2">
            {/* Issues had no tab because the page was behind a "Coming Soon"
                overlay; it was reachable only from a dashboard tile. Now that
                it is real, it belongs in the dock like everything else. */}
            <ul className="grid grid-cols-7 gap-1 rounded-2xl bg-white/80 backdrop-blur-xl ring-1 ring-black/5 shadow-lg shadow-black/10 p-2">
              <Tab to="/hoa-admin" icon={<Home className="w-5 h-5" />} label={t("nav.home")} />
              <Tab to="/hoa-admin/members" icon={<Users className="w-5 h-5" />} label={t("nav.members")} />
              <Tab to="/hoa-admin/payments" icon={<FileText className="w-5 h-5" />} label={t("nav.invoice")} />
              <Tab to="/hoa-admin/issues" icon={<Wrench className="w-5 h-5" />} label={t("nav.issues")} />
              <Tab to="/hoa-admin/communications" icon={<Megaphone className="w-5 h-5" />} label={t("nav.comms")} />
              <Tab to="/hoa-admin/reports" icon={<BarChart3 className="w-5 h-5" />} label={t("nav.reports")} />
              <Tab to="/hoa-admin/settings" icon={<Settings className="w-5 h-5" />} label={t("nav.settings")} />
            </ul>
          </div>
        </nav>
      </div>
    </PollingContext.Provider>
  );
}

function Tab({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <li>
      <NavLink
        to={to}
        end={to === "/hoa-admin"}
        className={[
          "group relative flex flex-col items-center justify-center rounded-xl py-2 transition",
          "focus-visible:ring-2 focus-visible:ring-[color:var(--brand)]/30 outline-none",
        ].join(" ")}
      >
        {({ isActive }: { isActive: boolean }) => (
          <>
            <span
              className="grid place-items-center transition-colors"
              style={{ color: isActive ? PRIMARY : "#4b5563" }}
            >
              {icon}
            </span>
            <span
              className="text-[11px] mt-1 font-medium transition-colors"
              style={{ color: isActive ? PRIMARY : "#4b5563" }}
            >
              {label}
            </span>
            <span
              aria-hidden
              className="absolute -bottom-0.5 h-1 w-6 rounded-full transition-opacity"
              style={{
                backgroundColor: PRIMARY,
                opacity: isActive ? 0.7 : 0,
              }}
            />
          </>
        )}
      </NavLink>
    </li>
  );
}

function titleFromPath(path: string, t: (key: string) => string) {
  if (path.includes("/members")) return t("hoa.layout.titles.members");
  if (path.includes("/payments")) return t("hoa.layout.titles.payments");
  if (path.includes("/communications")) return t("hoa.layout.titles.communications");
  if (path.includes("/reports")) return t("hoa.layout.titles.reports");
  if (path.includes("/settings")) return t("nav.settings");
  return t("nav.dashboard");
}
